"use client";

import { ArrowUpDown, Search, X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import {
  LISTING_SORTS,
  type FilterOption,
  type ListingFilterOptions,
  type ListingFilters,
  type ListingSort,
  type BrowserListing,
} from "./listing-filters";
import { ListingFiltersDialog } from "./listing-filters-dialog";

const QUICK_DISTANCE_MILES = 100;

function sortLabel(sort: ListingSort, hasDistance: boolean): string {
  switch (sort) {
    case "default":
      return hasDistance ? "Nearest" : "Recommended";
    case "price-asc":
      return "Price: low to high";
    case "price-desc":
      return "Price: high to low";
  }
}

function SortMenu({
  sort,
  hasDistance,
  onChange,
}: {
  sort: ListingSort;
  hasDistance: boolean;
  onChange: (sort: ListingSort) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" aria-label={`Sort by ${sortLabel(sort, hasDistance)}`}>
          <ArrowUpDown aria-hidden />
          <span className="hidden sm:inline">{sortLabel(sort, hasDistance)}</span>
          <span className="sm:hidden">Sort</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup
          value={sort}
          onValueChange={(value) => {
            const next = LISTING_SORTS.find((option) => option === value);
            if (next) onChange(next);
          }}
        >
          {LISTING_SORTS.map((option) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {sortLabel(option, hasDistance)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ListingSearch({
  query,
  onChange,
}: {
  query: string;
  onChange: (query: string) => void;
}) {
  return (
    <InputGroup className="sm:w-80 sm:shrink-0">
      <InputGroupAddon>
        <Search aria-hidden />
      </InputGroupAddon>
      <InputGroupInput
        type="search"
        aria-label="Search listings"
        placeholder="Search by title, city or grade"
        enterKeyHint="search"
        value={query}
        onChange={(event) => onChange(event.target.value)}
        className="[&::-webkit-search-cancel-button]:hidden"
      />
      {query ? (
        <InputGroupAddon align="inline-end">
          <InputGroupButton variant="ghost" size="icon-compact" aria-label="Clear search" onClick={() => onChange("")}>
            <X aria-hidden />
          </InputGroupButton>
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  );
}

function QuickToggle({
  label,
  count,
  isPressed,
  onToggle,
}: {
  label: string;
  count?: number;
  isPressed: boolean;
  onToggle: () => void;
}) {
  return (
    <Button variant="outline" aria-pressed={isPressed} onClick={onToggle}>
      {label}
      {count === undefined ? null : (
        <span aria-hidden className="text-muted-foreground tabular-nums">
          {count}
        </span>
      )}
    </Button>
  );
}

function toggled(values: readonly string[], value: string): string[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

function ConditionToggles({
  conditions,
  total,
  filters,
  onFiltersChange,
}: {
  conditions: readonly FilterOption[];
  total: number;
  filters: ListingFilters;
  onFiltersChange: (filters: ListingFilters) => void;
}) {
  const canNarrow = conditions.length > 1 || (conditions[0] !== undefined && conditions[0].count < total);
  if (!canNarrow) return null;
  return conditions.map((option) => (
    <QuickToggle
      key={option.value}
      label={option.label}
      count={option.count}
      isPressed={filters.conditions.includes(option.value)}
      onToggle={() => onFiltersChange({ ...filters, conditions: toggled(filters.conditions, option.value) })}
    />
  ));
}

type ListingBrowserControlsProps = {
  cards: readonly BrowserListing[] | null;
  listingsFailed: boolean;
  total: number;
  options: ListingFilterOptions;
  filters: ListingFilters;
  hasDistance: boolean;
  onFiltersChange: (filters: ListingFilters) => void;
};

export function listingCountLabel(resultCount: number, total: number): string {
  return resultCount === total
    ? `${total} ${total === 1 ? "listing" : "listings"}`
    : `${resultCount} of ${total} listings`;
}

export function ListingBrowserControls({
  cards,
  listingsFailed,
  total,
  options,
  filters,
  hasDistance,
  onFiltersChange,
}: ListingBrowserControlsProps) {
  const isNearbyOnly = filters.maxDistanceMiles === QUICK_DISTANCE_MILES;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">
        {hasDistance ? (
          <QuickToggle
            label={`Within ${QUICK_DISTANCE_MILES} mi`}
            isPressed={isNearbyOnly}
            onToggle={() =>
              onFiltersChange({ ...filters, maxDistanceMiles: isNearbyOnly ? null : QUICK_DISTANCE_MILES })
            }
          />
        ) : null}
        <ConditionToggles
          conditions={options.conditions}
          total={total}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </div>
      <ListingFiltersDialog
        cards={cards}
        listingsFailed={listingsFailed}
        options={options}
        filters={filters}
        hasDistance={hasDistance}
        onApply={onFiltersChange}
      />
      <SortMenu sort={filters.sort} hasDistance={hasDistance} onChange={(sort) => onFiltersChange({ ...filters, sort })} />
    </div>
  );
}
